import React, { useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import FlightRegistrationForm from './components/FlightRegistrationForm'
import FlightAdmin from './components/FlightAdmin'
import BusAdmin from './components/BusAdmin'
import ThankYou from './components/ThankYou'
import TicketConfirmation from './components/TicketConfirmation'
import PaymentSuccess from './components/PaymentSuccess'
import WaitlistCheckout from './components/WaitlistCheckout'
import WaitlistDecline from './components/WaitlistDecline'
import CancelConfirm from './components/CancelConfirm'
import CustomPay from './components/CustomPay'
import TariffPay from './components/TariffPay'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
    },
  },
})

function App() {
  const [showThankYou, setShowThankYou] = useState(false)
  const [registrationCode, setRegistrationCode] = useState('')

  const handleSubmitSuccess = (responseData) => {
    // Extract registration code from the API response
    if (responseData && responseData.registrationCode) {
      setRegistrationCode(responseData.registrationCode)
    }

    // If at capacity, show waitlist page
    if (responseData && responseData.isFull) {
      setShowThankYou(true)
      return
    }

    // If Banquest checkout URL returned, redirect to inline card form
    if (responseData && responseData.checkoutUrl) {
      sessionStorage.setItem('pendingRegistration', JSON.stringify({
        registrationCode: responseData.registrationCode,
        id: responseData.id,
        route: responseData.route || '',
        flightId: responseData.flightId || ''
      }))
      window.location.href = responseData.checkoutUrl
      return
    }

    // If Stripe URL returned, save registration info and redirect to payment
    if (responseData && responseData.paymentUrl) {
      sessionStorage.setItem('pendingRegistration', JSON.stringify({
        registrationCode: responseData.registrationCode,
        id: responseData.id,
        route: responseData.route || '',
        flightId: responseData.flightId || ''
      }))
      window.location.href = responseData.paymentUrl
      return
    }

    // Fallback: show thank you page
    setShowThankYou(true)
  }

  const handleBackToForm = () => {
    setShowThankYou(false)
    setRegistrationCode('')
  }

  // Shared registration page element
  const registrationPageElement = (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flight-header py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-6 mb-4">
              <img src="/igud-logo.png" alt="Igud Yeshivos" className="h-16 w-auto" />
              <img src="/ehk-logo_2-1.png" alt="Eretz Hakodesh" className="h-16 w-auto" />

            </div>
            <h1 className="text-3xl font-bold mb-1">Charter Flight Registration</h1>
            <h2 className="text-xl font-semibold opacity-90">Jerusalem &rarr; Milan (MXP) / New York</h2>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Registration Closed</h2>
          <div className="text-left max-w-xl mx-auto space-y-4 text-gray-700 leading-relaxed">
            <p>Thank you for your patience as we worked to arrange an additional flight. Over the past few days, we have been in close contact with the police and the head of the Israel Meteorological Service, holding out hope until the very last moment.</p>
            <p>Unfortunately, the only way to get everyone home before Shabbos was to leave on Wednesday, and given the weather situation that is not an option. We will therefore not be able to organize an additional flight.</p>
            <p>We feel terrible that we were not able to make this work for everyone. We wish your children a beautiful Yom Tov, and may we all see better and more peaceful times very soon.</p>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              For questions, please contact us at{' '}
              <a href="mailto:flights@igudys.com" className="text-blue-600 hover:text-blue-500">flights@igudys.com</a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-300">
            For questions about your registration, please contact us at{' '}
            <a href="mailto:flights@igudys.com" className="text-blue-400 hover:text-blue-300">
              flights@igudys.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  )

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* Admin Route */}
          <Route path="/admin" element={<FlightAdmin />} />
          <Route path="/buses" element={<BusAdmin />} />

          {/* Ticket Confirmation Route */}
          <Route path="/ticket/:uniqueId" element={<TicketConfirmation />} />

          {/* Payment Success Route */}
          <Route path="/payment-success/:registrationId" element={<PaymentSuccess />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />

          {/* Waitlist release routes */}
          <Route path="/checkout" element={<WaitlistCheckout />} />
          <Route path="/decline" element={<WaitlistDecline />} />
          <Route path="/cancel-confirm" element={<CancelConfirm />} />
          <Route path="/custom-pay" element={<CustomPay />} />
          <Route path="/tariff-pay" element={<TariffPay />} />

          {/* Registration Routes - Both "/" and "/register" point to the same place */}
          <Route path="/" element={registrationPageElement} />
          <Route path="/register" element={registrationPageElement} />
        </Routes>

        {/* Toast Notifications */}
        <ToastContainer
          position="bottom-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastStyle={{
            backgroundColor: '#1f2937',
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: '500'
          }}
          progressStyle={{
            backgroundColor: '#3b82f6'
          }}
        />
      </Router>
    </QueryClientProvider>
  )
}

export default App